import React, { useState, useEffect } from 'react';
import postMailImg from '@gumtree/ui-library/src/assets/is-post-mail.png';
import submitSuccessImg from '@gumtree/ui-library/src/assets/submit-success.png';
import submitFailImg from '@gumtree/ui-library/src/assets/post-fail.png';
import LineSteps from '@gumtree/ui-library/src/line-steps/line-steps';
import SubmitResultDialog from '@gumtree/ui-library/src/submit-result-dialog/submit-result-dialog';
import { SellerSkillItem } from '@gumtree/shared/src/model/listing.model';
import { trackGA4Event, trackGA4EventAfterPageView } from '@gumtree/shared/src/util/ga4-shared';
import debounce from '@gumtree/ui-library/src/utils/debounce';
import { RequestAQuoteDialog } from './index.style';
import QuoteDialogHeader from './quote-dialog-header';
import QuoteServiceForm, { SellerSkill } from './quote-service-form';
import DescribeForm from '../post-a-job-form/describe-form';
import ContactForm from '../post-a-job-form/contact-form';
import { getQaList, getIsRemote, submitLeadForm, getCompleteQaList } from '../../get-data/fetch/fetch-post-a-job';
import { useFormCache } from '../../hooks/useFormCache';
import { useFormHandlers } from '../../hooks/useFormHandlers';
import { buildBaseSubmitData, filterDescribeFormDataByQaList } from '../../utils/formUtils';

interface Props {
    isModalOpen: boolean;
    requestId: string;
    onCloseModal: () => void;
    profile: string;
    title: string;
    sellerSkills: SellerSkillItem[];
    categoryId: string;
    userId: string;
    currentPageUrl: string;
    preferredContactType: string;
    sellerId: string;
    sellerName: string;
    advertId: string;
    adCategoryId: number;
    app?: boolean;
}

interface SelectedService {
    value: string;
    displayName: string;
}


interface QaItem {
    title: string;
    type: string;
    questionId: string;
    answers: Array<{
        answerId: string;
        answerText: string;
    }>;
}

const RequestAQuoteForm: React.FC<Props> = ({
    isModalOpen,
    requestId,
    onCloseModal,
    profile,
    title,
    sellerSkills,
    categoryId,
    userId,
    currentPageUrl,
    preferredContactType,
    sellerId,
    sellerName,
    advertId,
    adCategoryId,
    app = false
}) => {
    const [loading, setLoading] = useState(false);
    const [skillsData, setSkillsData] = useState<SellerSkill[]>([]);
    const [moreQuotesStatus, setMoreQuotesStatus] = useState(false);
    const [isResultDialogOpen, setIsResultDialogOpen] = useState(false);
    const [resultMessage, setResultMessage] = useState<string>('');
    const [isSuccess, setIsSuccess] = useState<boolean>(true);
    const [qaList, setQaList] = useState<QaItem[]>([]);
    const [nationalWide, setNationalWide] = useState<string>('');
    const [shouldSubmitAfterUpdate, setShouldSubmitAfterUpdate] = useState(false);

    const {
        currentStep,
        setCurrentStep,
        selectedService,
        setSelectedService,
        describeFormData,
        setDescribeFormData,
        contactFormData,
        setContactFormData
    } = useFormCache<SelectedService>(app);

    const { handleDescribeFormDataChange, handleContactFormDataChange } = useFormHandlers(
        setDescribeFormData,
        setContactFormData
    );

    useEffect(() => {
        if (sellerSkills && sellerSkills.length > 0) {
            const transformed = sellerSkills.map(skill => ({
                disabled: false,
                displayValue: skill.description,
                selected: false,
                value: skill.id.toString()
            }));

            setSkillsData(transformed);
        } else {
            setSkillsData([]);
        }
    }, [sellerSkills]);

    useEffect(() => {
        
        if (isModalOpen && requestId && advertId && adCategoryId && preferredContactType) {
            const linkText = preferredContactType === 'call' ? 'request call back' : 'request a quote';

            trackGA4EventAfterPageView<any>({
                event: 'form_show',
                formName: 'bark_lead',
                formStep: 'select service',
                linkText,
                requestId,
                listingId: advertId,
                adCategoryId: adCategoryId
            });
        }
    }, [isModalOpen, requestId, advertId, adCategoryId, preferredContactType]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const qaData = await getQaList(parseInt(categoryId));
                setQaList(qaData);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        if (isModalOpen && categoryId) {
            fetchData();
        }
    }, [isModalOpen, categoryId]);

    useEffect(() => {
        if (shouldSubmitAfterUpdate && contactFormData.isMoreProviders !== undefined) {
            handleSubmitForm();
            setShouldSubmitAfterUpdate(false);
        }
    }, [contactFormData.isMoreProviders, shouldSubmitAfterUpdate]);

    const modalHeader = () => {
        return <QuoteDialogHeader profile={profile} title={title} />;
    };

    const handleServiceSelection = (value: string, displayName: string) => {
        setSelectedService({ value, displayName });

        const updatedSkills = skillsData.map(skill => ({
            ...skill,
            selected: skill.value === value
        }));
        
        const isChanged = updatedSkills.some((s, i) => s.selected !== skillsData[i].selected);
        if (isChanged) {
            setSkillsData(updatedSkills);
        }
    };

    const goToStep = (step: number) => {
        setCurrentStep(step);
    };

    const handleServiceContinue = debounce(async () => {
        const isRemoteData = await getIsRemote(Number(selectedService?.value));
        const completeQaList = await getCompleteQaList(Number(selectedService?.value));
        setNationalWide(isRemoteData);
        setQaList(completeQaList);
        // 进入 DescribeForm 前，基于最新的 qaList 过滤 describeFormData
        setDescribeFormData(prev => filterDescribeFormDataByQaList(prev, completeQaList));
        goToStep(2);
    }, 300);

    const handleDescribeBack = () => {
        goToStep(1);
    };

    const handleDescribeNext = () => {
        goToStep(3);
    };

    const handleContactBack = () => {
        goToStep(2);
    };

    const handleSendRequest = () => {
        if (contactFormData.isMoreProviders) {
            handleSubmitForm();
        } else {
            setMoreQuotesStatus(true);
            setResultMessage('Service providers can be busy. Getting more quotes means more chances to get your request done.')
            setIsResultDialogOpen(true);
        }
    };

    const onResultDialogCloseModal = debounce(() => {
        if (loading) return;
        setIsResultDialogOpen(false);
        if (!moreQuotesStatus) {
            // Reset dialog-related states
            setMoreQuotesStatus(false);
            setResultMessage('');
            setIsSuccess(true);
            onCloseModal();
        }
    }, 300);

    const handleResultDialogClick = debounce(() => {
        if (loading) return;
        if (moreQuotesStatus) {
            setContactFormData(prev => ({
                ...prev,
                isMoreProviders: true
            }));
            setShouldSubmitAfterUpdate(true);
        } else {
            setIsResultDialogOpen(false);
            onCloseModal();
        }
    }, 300);

    const handleDialogExtraButtonClick = debounce(() => {
        if (loading) return;
        setContactFormData(prev => ({
            ...prev,
            isMoreProviders: false
        }));
        setShouldSubmitAfterUpdate(true);
    }, 300);

    const trackFormSubmission = (isSuccess: boolean) => {
        const formStep = isSuccess ? 'success' : 'failed';
        const formValidation = isSuccess ? 'success' : 'failure';
        const linkText = preferredContactType === 'call' ? 'request call back' : 'request a quote';
        
        trackGA4Event<GA4.SubmitForm>({
            event: 'form_submit',
            formName: 'bark_lead',
            formStep,
            formValidation,
            linkText,
            requestId,
            attributionsValue: JSON.stringify({
                moreQuotes: contactFormData.isMoreProviders
            }),
            listingId: advertId,
            adCategoryId: adCategoryId.toString()
        });
        
        trackGA4Event<GA4.SubmitForm>({
            event: 'bark_lead_submit',
            formName: 'bark_lead',
            formStep,
            formValidation,
            linkText,
            requestId,
            attributionsValue: JSON.stringify({
                moreQuotes: contactFormData.isMoreProviders
            }),
            listingId: advertId,
            adCategoryId: adCategoryId.toString()
        });
    };

    const handleSubmitForm = async () => {
        setLoading(true);
        try {
            const baseSubmitData = buildBaseSubmitData(
                describeFormData,
                contactFormData,
                requestId,
                userId,
                currentPageUrl,
                categoryId,
                selectedService,
                'request a quote'
            );

            const submitData = {
                ...baseSubmitData,
                isMoreQuotes: contactFormData.isMoreProviders,
                preferredContactType,
                sellerId,
                sellerName,
                advertId
            };
            
            const result = await submitLeadForm(submitData);
            
            setIsResultDialogOpen(true);
            setMoreQuotesStatus(false);

            if (['00', '04'].includes(result?.code)) {
                trackFormSubmission(true);

                setIsSuccess(true);
                setResultMessage(`All done!\nYou'll hear back from ${contactFormData.isMoreProviders ? 'up to 5 available providers' : 'the provider'} within 72 hours.`);
                // Reset all form data
                const updatedSkills = skillsData.map(skill => ({
                    ...skill,
                    selected: false
                }));
                setSkillsData(updatedSkills);
                setSelectedService(null);
                setDescribeFormData({});
                setContactFormData({
                    isMoreProviders: true
                });
                setCurrentStep(1);
            } else {
                trackFormSubmission(false);
                
                setCurrentStep(1);
                setIsSuccess(false);
                // TODO: uncomment this when the text is updated
                // setResultMessage(result?.msg || 'We had trouble submitting your form.\nPlease try again.');      
                setResultMessage('We had trouble submitting your form.\nPlease try again.');
            }
        } catch (error) {
            trackFormSubmission(false);
            
            console.error('Error submitting form:', error);
            setIsResultDialogOpen(true);
            setIsSuccess(false);
            setResultMessage('We had trouble submitting your form.\nPlease try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleRequestAQuoteDialogClose = () => {
        setCurrentStep(1);
        onCloseModal();
    };

    return (
        <>
            <RequestAQuoteDialog
                isOpen={isModalOpen}
                hideCloseBtn={app}
                onClose={handleRequestAQuoteDialogClose}
                title={modalHeader()}
                displayAsSheetOnMweb
                app={app}
            >
                <LineSteps total={3} current={currentStep} />
                {currentStep === 1 && (
                    <QuoteServiceForm 
                        sellerSkills={skillsData}
                        elName="quote-service"
                        onSelectionChange={handleServiceSelection}
                        onContinue={handleServiceContinue}
                    />
                )}
                {currentStep === 2 && (
                    <DescribeForm 
                        subDescription="Provide details about your job including any issues, schedules and budget."
                        onBack={handleDescribeBack}
                        onNext={handleDescribeNext}
                        formData={describeFormData}
                        onFormDataChange={handleDescribeFormDataChange}
                        qaList={qaList}
                        nationalWide={nationalWide}
                        app={app}
                    />
                )}
                {currentStep === 3 && (
                    <ContactForm 
                        onBack={handleContactBack}
                        formData={contactFormData}
                        onFormDataChange={handleContactFormDataChange}
                        onSubmit={handleSendRequest}
                        isRequestAQuote
                        loading={loading}
                        app={app}
                    />
                )}
            </RequestAQuoteDialog>
            <SubmitResultDialog
                isOpen={isResultDialogOpen}
                resultMode={!moreQuotesStatus}
                onClose={onResultDialogCloseModal}
                imageSrc={moreQuotesStatus ? postMailImg : isSuccess ? submitSuccessImg : submitFailImg}
                imageAlt={moreQuotesStatus ? "Post mail" : isSuccess ? "Success" : "Failed"}
                title={moreQuotesStatus ? "You've chosen one provider.\nWant to hear from others too?" : isSuccess ? "Job shared" : "Uh-oh! Something went wrong"}
                description={resultMessage}
                buttonDisabled={loading}
                buttonText={(loading && contactFormData.isMoreProviders) ? "Sending..." : moreQuotesStatus ? "Get more quotes" : isSuccess ? "Got it" : "Try again later"}
                onButtonClick={handleResultDialogClick}
                extraButtonText={(loading && !contactFormData.isMoreProviders) ? "Sending..." : "No, just one"}
                extraButtonDisabled={loading}
                onExtraButtonClick={handleDialogExtraButtonClick}
            />
        </>
    );
};

export default RequestAQuoteForm;
